// Projekt-Health-Ampel — rein berechnet, keine Persistenz.
//
// Bewertet ein Projekt anhand der Kennzahlen, die überall verfügbar sind
// (Projektstatus, Start-/Zieltermin, erledigte vs. gesamte Aufgaben). Dadurch
// zeigen Projekt-Übersicht (ProjectCard) und Projekt-Detail-Kopf DIESELBE
// Ampel — es gibt keine zusätzlichen Queries und keine DB-Spalte.
//
// Grundidee "Schedule Performance": vergleicht den zeitlichen Fortschritt
// (verstrichener Anteil zwischen Start und Ziel) mit dem inhaltlichen
// Fortschritt (erledigte Aufgaben). Wer zeitlich deutlich vor dem inhaltlichen
// Fortschritt liegt, ist in Verzug.

import { localTodayIso } from "./format";

export type HealthLevel = "green" | "yellow" | "red" | "neutral";

export interface ProjectHealth {
  level: HealthLevel;
  /** Kurzlabel für Badge/Tooltip, z. B. "Auf Kurs". */
  label: string;
  /** Ein-Satz-Begründung für den Tooltip. */
  reason: string;
}

export interface HealthInput {
  status: string;
  start_date: string | null;
  target_date: string | null;
  tasks_total: number;
  tasks_done: number;
}

/** Anzahl ganzer Tage von `fromIso` (inkl.) bis `toIso`. Positiv = Zukunft. */
function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00`).getTime();
  const to = new Date(`${toIso}T00:00:00`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.round((to - from) / 86_400_000);
}

export function computeProjectHealth(p: HealthInput): ProjectHealth {
  const today = localTodayIso();
  const total = p.tasks_total;
  const done = p.tasks_done;
  const allDone = total > 0 && done >= total;
  const progress = total > 0 ? done / total : 0;

  // Nicht-aktive Zustände bewerten wir nicht als Risiko.
  if (p.status === "done") {
    return { level: "green", label: "Abgeschlossen", reason: "Projekt ist abgeschlossen." };
  }
  if (p.status === "archived") {
    return { level: "neutral", label: "Archiviert", reason: "Projekt ist archiviert." };
  }
  if (p.status === "paused") {
    return { level: "neutral", label: "Pausiert", reason: "Projekt ist pausiert." };
  }

  // Überfällig: Zieltermin überschritten und noch nicht alles erledigt.
  if (p.target_date && p.target_date < today && !allDone) {
    const overdueDays = Math.abs(daysBetween(p.target_date, today));
    return {
      level: "red",
      label: "Kritisch",
      reason: `Zieltermin seit ${overdueDays} Tag(en) überschritten, erst ${Math.round(progress * 100)} % erledigt.`,
    };
  }

  // Zeitlicher Fortschritt zwischen Start und Ziel (0..1).
  let elapsedFrac: number | null = null;
  if (p.start_date && p.target_date) {
    const span = daysBetween(p.start_date, p.target_date);
    if (span > 0) {
      elapsedFrac = Math.min(1, Math.max(0, daysBetween(p.start_date, today) / span));
    }
  }

  const daysToTarget = p.target_date ? daysBetween(today, p.target_date) : null;
  const nearDeadline = daysToTarget !== null && daysToTarget <= 7;

  if (elapsedFrac !== null) {
    const lag = elapsedFrac - progress; // >0 = inhaltlich hinter dem Zeitplan
    if (elapsedFrac >= 0.5 && lag > 0.35) {
      return {
        level: "red",
        label: "Kritisch",
        reason: `Deutlich hinter dem Zeitplan: ${Math.round(elapsedFrac * 100)} % der Zeit verbraucht, ${Math.round(progress * 100)} % erledigt.`,
      };
    }
    if (lag > 0.2 || (nearDeadline && progress < 0.9)) {
      return {
        level: "yellow",
        label: "Beobachten",
        reason: `Hinter dem Zeitplan: ${Math.round(elapsedFrac * 100)} % der Zeit verbraucht, ${Math.round(progress * 100)} % erledigt.`,
      };
    }
    return {
      level: "green",
      label: "Auf Kurs",
      reason: `Im Plan: ${Math.round(progress * 100)} % erledigt bei ${Math.round(elapsedFrac * 100)} % verbrauchter Zeit.`,
    };
  }

  // Kein auswertbarer Zeitrahmen: nur die Nähe zum Zieltermin nutzen.
  if (nearDeadline && progress < 0.9) {
    return {
      level: "yellow",
      label: "Beobachten",
      reason: `Zieltermin in ${daysToTarget} Tag(en), erst ${Math.round(progress * 100)} % erledigt.`,
    };
  }
  if (!p.target_date) {
    return { level: "neutral", label: "Kein Zieltermin", reason: "Ohne Zieltermin keine Terminbewertung möglich." };
  }
  return {
    level: "green",
    label: "Auf Kurs",
    reason: `${Math.round(progress * 100)} % erledigt, Zieltermin in ${daysToTarget} Tag(en).`,
  };
}
